import { useCallback, useId, useMemo, useState } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { Button, Input, SelectableChip, TextArea } from '@aimsos/ui';

import { INTERACTION_OUTCOMES } from '../../constants/history.js';
import InteractionAttachments from './InteractionAttachments.jsx';

/** A duration in MINUTES: "12", "12 min", "12m", "12 minutes". "1h" or "1.5" is not one. */
const MINUTES = /^(\d+)\s*(m|min|mins|minutes?)?$/i;

/** The minutes typed, or `null` for anything that is not a duration in minutes. */
const parseMinutes = (value) => {
  const match = MINUTES.exec(String(value).trim());
  return match && Number(match[1]) > 0 ? Number(match[1]) : null;
};

/**
 * A call or a meeting logged after the fact (CT-5724), as in the prototype:
 * what it was about (prefilled with "Call with <name>"), how it went (the
 * outcome chips of its kind), then the optional duration and what was said,
 * files and links. No date — it is logged now — and no scope field ("el scope lo
 * voy a quitar").
 *
 * The submit button names the action ("Log call") and stays disabled until the
 * subject and the outcome are given; the reason is shown next to it and wired
 * with `aria-describedby`.
 *
 * @param {object} props
 * @param {'call'|'meeting'} props.kind
 * @param {string} props.defaultSubject — "Call with <name>".
 * @param {boolean} props.saving
 * @param {(values: object) => Promise<boolean>} props.onSubmit
 * @param {() => void} props.onCancel
 */
function InteractionForm({ kind, defaultSubject, saving, onSubmit, onCancel }) {
  const { t } = useTranslation('unified-profiles');
  const fieldId = useId();
  const reasonId = useId();
  const outcomeLabelId = useId();
  const [subject, setSubject] = useState(defaultSubject ?? '');
  const [outcome, setOutcome] = useState(null);
  const [duration, setDuration] = useState('');
  const [summary, setSummary] = useState('');
  const [files, setFiles] = useState([]);
  const [urls, setUrls] = useState([]);

  const handleSubject = useCallback((event) => setSubject(event.target.value), []);
  const handleDuration = useCallback((event) => setDuration(event.target.value), []);
  const handleSummary = useCallback((event) => setSummary(event.target.value), []);
  const addFiles = useCallback(
    (picked) => setFiles((current) => [...current, ...picked.filter((file) => !current.some((c) => c.id === file.id))]),
    [],
  );
  const removeFile = useCallback((id) => setFiles((current) => current.filter((file) => file.id !== id)), []);
  const addUrl = useCallback((url) => setUrls((current) => (current.includes(url) ? current : [...current, url])), []);
  const removeUrl = useCallback((url) => setUrls((current) => current.filter((candidate) => candidate !== url)), []);

  // The duration is optional, but what is typed must be minutes — "1h" is not 1 minute.
  const invalidReason = useMemo(() => {
    if (!subject.trim()) return 'history.form.reason.subject';
    if (!outcome) return 'history.form.reason.outcome';
    if (duration.trim() && parseMinutes(duration) === null) return 'history.form.reason.duration';
    return null;
  }, [subject, outcome, duration]);

  const canSave = !invalidReason && !saving;
  const handleSubmit = useCallback(
    (event) => {
      event.preventDefault();
      if (!canSave) return;
      onSubmit({
        subject: subject.trim(),
        outcome,
        durationMinutes: parseMinutes(duration),
        summary: summary.trim(),
        attachments: files,
        urls,
      });
    },
    [canSave, onSubmit, subject, outcome, duration, summary, files, urls],
  );

  return (
    <form onSubmit={handleSubmit} noValidate data-testid="interaction-form" data-kind={kind} className="flex min-h-0 flex-col gap-5">
      <Input
        id={`${fieldId}-subject`}
        label={t('history.form.subject')}
        value={subject}
        onChange={handleSubject}
        placeholder={t('history.form.subjectPlaceholder')}
        disabled={saving}
        required
      />

      <div className="flex flex-col gap-2">
        <span id={outcomeLabelId} className="text-sm font-medium text-content-secondary">
          {t('history.form.outcome')}
        </span>
        <div role="group" aria-labelledby={outcomeLabelId} className="flex flex-wrap gap-2">
          {INTERACTION_OUTCOMES[kind].map((value) => (
            <SelectableChip key={value} selected={outcome === value} onClick={() => setOutcome(value)} disabled={saving}>
              {t(`history.form.outcomes.${value}`)}
            </SelectableChip>
          ))}
        </div>
      </div>

      <Input
        id={`${fieldId}-duration`}
        label={t('history.form.duration')}
        value={duration}
        onChange={handleDuration}
        placeholder={t('history.form.durationPlaceholder')}
        inputMode="numeric"
        disabled={saving}
      />
      <div className="flex flex-col gap-1.5">
        <label htmlFor={`${fieldId}-summary`} className="text-sm font-medium text-content-secondary">
          {t('history.form.summary')}
        </label>
        <TextArea
          id={`${fieldId}-summary`}
          rows={5}
          value={summary}
          onChange={handleSummary}
          placeholder={t('history.form.summaryPlaceholder')}
          disabled={saving}
        />
      </div>

      <InteractionAttachments
        files={files}
        urls={urls}
        disabled={saving}
        onAddFiles={addFiles}
        onRemoveFile={removeFile}
        onAddUrl={addUrl}
        onRemoveUrl={removeUrl}
      />

      <div className="flex flex-wrap items-center justify-end gap-3 border-t border-edge pt-4">
        {invalidReason ? (
          <span id={reasonId} className="mr-auto text-xs text-content-muted">
            {t(invalidReason)}
          </span>
        ) : null}
        <Button variant="secondary" size="md" onClick={onCancel} disabled={saving}>
          {t('history.form.cancel')}
        </Button>
        <Button type="submit" variant="primary" size="md" disabled={!canSave} aria-describedby={invalidReason ? reasonId : undefined}>
          {t(`history.log.${kind}.submit`)}
        </Button>
      </div>
    </form>
  );
}

export default InteractionForm;
